import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import PulsingLoader, {
  LoaderHolder,
  LoaderTextHolder,
  LoaderTitle,
} from '@/components/PulsingLoader';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import Card from '@/pages/MLProject/components/Card';
import { MLProjectTranslation } from '@/types/resources';
import { api } from '@/utils/api';
import {
  MLProjectTranslationStatus,
  MLProjectTranslationVideoStatus,
} from '@/utils/enums';

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;

const Video = styled.video`
  width: 100%;
  height: auto;
  max-height: 500px;
  margin-top: 16px;

  &::-webkit-media-controls-panel {
    background-image: linear-gradient(transparent, transparent) !important;
  }
`;

const StyledLoaderHolder = styled(LoaderHolder)`
  margin: 0;
`;

interface Props {
  translation: MLProjectTranslation;
}

const PreviewVideoCard: FC<Props> = ({ translation }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onApprove = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        'Approve final video and move to distribution drafts',
      );

      if (!confirmed) {
        return;
      }

      await api.post(
        `/v1/distribution/ml-translations/${translation.id}/approve`,
      );

      await queryClient.invalidateQueries([
        'ml_translations',
        {
          id: translation.id.toString(),
        },
      ]);

      alerts.success(
        'Video successfuly approved and moved to distribution drafts',
      );
    } catch {
      alerts.error();
    }
  };

  const renderContent = () => {
    if (
      translation.videoStatus === MLProjectTranslationVideoStatus.GENERATING
    ) {
      return (
        <StyledLoaderHolder>
          <PulsingLoader />
          <LoaderTextHolder>
            <LoaderTitle $type="button">Generating final video ...</LoaderTitle>
            <Text $color="gray" $type="captionSemibold">
              Please be patient, this action may take
              <br />a little longer in some cases
            </Text>
          </LoaderTextHolder>
        </StyledLoaderHolder>
      );
    }

    if (translation.videoStatus === MLProjectTranslationVideoStatus.DONE) {
      return (
        <>
          <Text $type="smallBold">{translation.language} Language</Text>
          <Video key={translation.video!.id} controls>
            <source
              src={translation.video!.versions.original}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </Video>
          <Footer>
            <Button
              onClick={onApprove}
              disabled={translation.status === MLProjectTranslationStatus.DONE}
            >
              Approve
            </Button>
            <Button
              color="secondary"
              as="a"
              target="_blank"
              rel="noreferrer noopener"
              href={translation.video!.versions.original}
            >
              Download
            </Button>
          </Footer>
        </>
      );
    }

    if (translation.videoStatus === MLProjectTranslationVideoStatus.FAILED) {
      return (
        <StyledLoaderHolder>
          <LoaderTitle $type="button">
            An error ocurred while generating video.
            <br />
            Please try again
          </LoaderTitle>
        </StyledLoaderHolder>
      );
    }

    return (
      <StyledLoaderHolder>
        <LoaderTitle $type="button">
          The final video has not been generated yet
        </LoaderTitle>
      </StyledLoaderHolder>
    );
  };

  return (
    <Card
      storageKey={`ml_translation_${translation.id}_preview_card_visibility`}
      title="Preview Video"
      description="Preview the merged video and voiceover to approve them for distribution"
      defaultIsOpened
    >
      {renderContent()}
    </Card>
  );
};

export default PreviewVideoCard;
